import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';

import {
	CHART_SCOPE,
	chartRefusal,
	createChart,
	deleteChart,
	demoteChart,
	getChartLists,
	promoteChart,
	updateChart,
} from '../services/charts';
import { normalizeId } from '../utils/ids';

/**
 * One collection's saved charts for the Reports page (charts A3, plan §6.1):
 * the collection's own — `charts`, the collection tier and the caller's own.
 * A sub-collection inherits none from the collections above it (KPR-1084).
 *
 * No provider: only Reports lists charts, so a fetch in this hook is the
 * honest amount of machinery (the saved-filters idiom, useSavedFilters).
 * Lists are never run here — running every chart to list it would be the
 * fan-out the batch run exists to prevent; a row is text.
 *
 * Writes wait for the server, which answers the chart as it now is (its
 * tier, its `canEdit`), and a refused write leaves the list as it was. A
 * delete is the one optimistic write: the row goes at once and comes back if
 * the server refuses. `rename` and `duplicate` reject with the server's
 * refusal so the naming window can show a clash ("You already have a chart
 * with that name here.") where the person is still typing.
 */

const tierRank = (scope) => (scope === CHART_SCOPE.COLLECTION ? 0 : 1);

/** The collection tier first, then the caller's own, by name within each — the server's own order. */
export const sortCharts = (list) => [...list].sort((a, b) => (
	tierRank(a?.scope) - tierRank(b?.scope)
	|| (a?.name || '').localeCompare(b?.name || '')
));

export default function useCharts(collectionId, { enabled = true } = {}) {
	const { message } = App.useApp();
	const [charts, setCharts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [loadToken, setLoadToken] = useState(0);
	// The collection a first read has answered for (success or failure). Until
	// it has, `loading` stays true even on the render before the effect runs, so
	// a list never draws its empty state ahead of its first answer (§14).
	const [settledFor, setSettledFor] = useState(null);
	// The committed list, for a delete's rollback (a state updater may run lazily).
	const chartsRef = useRef(charts);
	chartsRef.current = charts;

	useEffect(() => {
		if (!collectionId || !enabled) {
			setCharts([]);
			setError(null);
			setLoading(false);
			return undefined;
		}
		let active = true;
		setLoading(true);
		setError(null);
		getChartLists(collectionId)
			.then(({ charts: own }) => {
				if (!active) return;
				setCharts(sortCharts(own));
			})
			.catch((e) => {
				if (!active) return;
				setCharts([]);
				setError(e);
			})
			.finally(() => {
				if (!active) return;
				setLoading(false);
				setSettledFor(collectionId);
			});
		return () => { active = false; };
	}, [collectionId, enabled, loadToken]);

	const reload = useCallback(() => setLoadToken((n) => n + 1), []);

	const replace = useCallback((updated) => {
		if (!updated || !updated._id) return;
		const id = normalizeId(updated._id);
		setCharts((current) => {
			const known = current.some((c) => normalizeId(c?._id) === id);
			return sortCharts(known ? current.map((c) => (normalizeId(c?._id) === id ? { ...c, ...updated } : c)) : [...current, updated]);
		});
	}, []);

	/** Rename (and describe): rejects with the refusal, for the window to say. */
	const rename = useCallback(async (chart, { name, description }) => {
		const { warnings, ...updated } = await updateChart(collectionId, normalizeId(chart?._id), { name, description }) || {};
		replace(updated);
		return updated;
	}, [collectionId, replace]);

	/** A personal copy under a new name: rejects with the refusal, for the window to say. */
	const duplicate = useCallback(async (chart, { name, description }) => {
		const { warnings, ...created } = await createChart(collectionId, { name, description, spec: chart?.spec }) || {};
		replace(created);
		return created;
	}, [collectionId, replace]);

	const remove = useCallback(async (chart) => {
		const id = normalizeId(chart?._id);
		const snapshot = chartsRef.current;
		setCharts((current) => current.filter((c) => normalizeId(c?._id) !== id));
		try {
			await deleteChart(collectionId, id);
			return true;
		} catch (e) {
			setCharts(snapshot);
			message.error(chartRefusal(e, 'Could not delete the chart.'));
			return false;
		}
	}, [collectionId, message]);

	const promote = useCallback(async (chart) => {
		try {
			const updated = await promoteChart(collectionId, normalizeId(chart?._id));
			replace(updated);
			return updated;
		} catch (e) {
			message.error(chartRefusal(e, 'Could not pin the chart for everyone.'));
			return null;
		}
	}, [collectionId, message, replace]);

	const demote = useCallback(async (chart) => {
		try {
			const updated = await demoteChart(collectionId, normalizeId(chart?._id));
			replace(updated);
			return updated;
		} catch (e) {
			message.error(chartRefusal(e, 'Could not unpin the chart.'));
			return null;
		}
	}, [collectionId, message, replace]);

	const waiting = loading || Boolean(collectionId && enabled && settledFor !== collectionId);

	return { charts, loading: waiting, error, reload, rename, duplicate, remove, promote, demote };
}
